import React, { useCallback, useEffect, useMemo, useState } from 'react'
import * as yup from 'yup'
import message from 'services/message'
import { resolveErrorMessage } from 'helpers'
import IconTicketSend from 'ui/Icons/IconTicketSend'
import { parseStringToInt } from 'helpers/format'

// Services
import event from 'services/event'
import dialog from 'services/dialog'

// Use
import useForm from 'use/form'
import useUploadHook from 'use/upload'
import useQuery from 'use/query'

// Apis
import { createTicket, getTicketCategoryList, uploadFile } from 'api/ticket'

// Components
import Col from 'ui/Col'
import Row from 'ui/Row'
import Text from 'ui/Text'
import Box from 'ui/Box'
import Select from 'ui/Form/Select'
import Input from 'ui/Form/Input'
import FileHelp from 'ui/FileHelp'
import FileDropZone from 'ui/FileDropZone'
import FileItem from 'ui/FileItem'
import Button from 'ui/Button'
import SelectContract from 'components/Payment/ContractFilter/SelectContract'
import SelectCompany from 'components/Payment/ContractFilter/SelectCompany'
import SelectLessor from 'components/Payment/ContractFilter/SelectLessor'
import SelectVendor from 'components/Payment/ContractFilter/SelectVendor'
import Space from 'ui/Space'
import HTMLViewer from 'ui/HTMLViewer'
import Tooltip from 'ui/Tooltip'
import IconInfomation from 'ui/Icons/IconInfomation'

import { EVENT_TICKET_CREATED } from 'constants/events'
import useMobile from 'ui/Datatable/mobile'

const accept = '.xls,.xlsx,.pdf,.jpg'
const multiple = true
const maxFileSize = 20
const totalSize = 20

const defaultValue = {
  lessorId: '',
  companyId: '',
  companyCode: '',
  vendorId: '',
  vendorCode: '',
  contractId: '',
  category: '',
  type: '',
  concern: '',
}

export const TicketForm = () => {
  const isMobile = useMobile()

  const {
    lessorId,
    companyId,
    companyCode,
    vendorId,
    vendorCode,
    contractNumber,
    ecwtId,
  } = useQuery()

  const formInitialValue = useMemo(() => {
    if (
      lessorId &&
      companyId &&
      companyCode &&
      vendorId &&
      vendorCode &&
      contractNumber
    ) {
      return {
        lessorId: parseStringToInt(lessorId),
        companyId: parseStringToInt(companyId),
        companyCode,
        vendorId: parseStringToInt(vendorId),
        vendorCode,
        contractId: contractNumber,
        category: '',
        type: '',
        concern: '',
      }
    }

    return defaultValue
  }, [companyCode, companyId, contractNumber, lessorId, vendorCode, vendorId])

  // States
  const [categoryOptions, setCategoryOptions] = useState([])
  const [types, setTypes] = useState([])

  // Validation schema
  const validationSchema = yup.object().shape({
    lessorId: yup.string().nullable().required('Please select lessor name'),
    companyId: yup.string().nullable().required('Please select company name'),
    companyCode: yup.string(),
    vendorId: yup.string().nullable().required('Please select vendor code'),
    vendorCode: yup.string(),
    contractId: yup.string().nullable().required('Please select contract no'),
    category: yup.string().nullable().required('Please select category'),
    type: yup.string().nullable().required('Please select type'),
    concern: yup
      .string()
      .nullable()
      .trim()
      .required('Please enter your message'),
  })

  const onUpload = async ({ file, onUploadProgress, onSuccess, onError }) => {
    try {
      const data = await uploadFile({
        file,
        onUploadProgress,
      })

      onSuccess({
        name: data,
      })
    } catch (error) {
      onError(error)
    }
  }

  // Upload hook
  const { files, uploading, removeFile, onFileChange, setFiles } =
    useUploadHook({
      maxFileSize,
      totalSize,
      accept,
      allowDuplicate: true,
      autoupload: true,
      multiple,
      onUpload,
    })

  const filesHasError = useMemo(() => {
    return files.some((x) => x.status != 'done')
  }, [files])

  // UseFormHook
  const {
    form,
    errors,
    validate,
    onChange,
    setForm,
    reset,
    setFieldValue,
    setFieldError,
  } = useForm({
    schema: validationSchema,
    initialForm: formInitialValue,
  })

  const toolTipContent = useMemo(() => {
    if (!form.type) {
      return ''
    }

    return types.find((x) => x.typeId === form.type).toolTip
  }, [form.type, types])

  const typeOptions = useMemo(() => {
    if (!form.category) {
      return []
    }
    return types
      .filter((x) => x.category === form.category)
      .map((x) => {
        return {
          value: x.typeId,
          label: x.typeName,
        }
      })
  }, [form.category, types])

  const typePlaceholder = useMemo(() => {
    if (!form.category) {
      return 'Select type'
    }

    if (typeOptions.length === 1 && !typeOptions[0].label) {
      return 'N/A'
    }

    return 'Select type'
  }, [form.category, typeOptions])

  useEffect(() => {
    if (typeOptions.length === 1) {
      setFieldValue('type', typeOptions[0].value)
    }
  }, [setFieldValue, typeOptions])

  useEffect(() => {
    getTicketCategoryList()
      .then(({ categoryList, typeList }) => {
        setCategoryOptions(categoryList)
        setTypes(typeList)
      })
      .catch(() => {})
  }, [])

  const onLessorChange = useCallback(
    (e) => {
      onChange(e, 'lessorId')
      onChange('', 'companyId')
      onChange('', 'companyCode')
      onChange('', 'vendorId')
      onChange('', 'vendorCode')
      onChange('', 'contractId')
    },
    [onChange]
  )

  const onCompanyChange = useCallback(
    (e, name, companyCode) => {
      onChange(e, 'companyId')
      onChange(companyCode, 'companyCode')
      onChange('', 'vendorId')
      onChange('', 'vendorCode')
      onChange('', 'contractId')
    },
    [onChange]
  )

  const onVendorChange = useCallback(
    (e, name, vendorCode) => {
      onChange(e, 'vendorId')
      onChange(vendorCode, 'vendorCode')
      onChange('', 'contractId')
    },
    [onChange]
  )

  const onContractChange = useCallback(
    (e) => {
      onChange(e, 'contractId')
    },
    [onChange]
  )

  const onCategoryChange = useCallback(
    (e) => {
      onChange(e, 'category')

      setFieldValue('type', '')
      setFieldError('type', '')
    },
    [onChange, setFieldValue, setFieldError]
  )

  const onSubmit = async () => {
    try {
      await validate()

      if (filesHasError) {
        return
      }

      dialog.confirm({
        title: 'Send ticket?',
        message: (
          <>
            <Text color="#000000">
              Are you sure you want to send this ticket to our
            </Text>
            <Text color="#000000" paddingBottom={10}>
              support team?
            </Text>
          </>
        ),
        onConfirm: async ({ setLoading, setVisible: setConfirmVisible }) => {
          // Show loading
          setLoading(true)

          try {
            await createTicket({
              lessorId: form.lessorId,
              companyCode: form.companyCode,
              vendorCode: form.vendorCode,
              contractNumber: form.contractId,
              typeId: form.type,
              attachmentFileNames: files.map((x) => x.name),
              concern: form.concern,
              ecwtId,
            })
            setForm(formInitialValue)
            setFiles([])
            setTimeout(() => {
              reset()
            }, 0)
            setConfirmVisible(false)
            event.emit(EVENT_TICKET_CREATED)

            dialog.alert({
              title: 'Ticket sent!',
              icon: <IconTicketSend />,
              message: (
                <>
                  <Text color="#000000">
                    Your message was sent to our support team.
                  </Text>
                  <Text color="#000000" paddingBottom={10}>
                    We’ll get back to you within 24 to 48 business hours.
                  </Text>
                </>
              ),
              onConfirm({ setVisible: setAlertVisible }) {
                setAlertVisible(false)
              },
            })
          } catch (error) {
            message.error(await resolveErrorMessage(error))
          } finally {
            setLoading(false)
          }
        },
      })
    } catch (error) {
      //
    }
  }

  return (
    <Box id="form-create-ticket" shadow>
      <Row multiline>
        <Col sm={12}>
          <Text bold color="#000000">
            For which contract is your message about?
          </Text>
        </Col>
        <Col xs={12} md={6}>
          <SelectLessor
            name="lessorId"
            value={form.lessorId}
            errors={errors}
            onChange={onLessorChange}
          />
        </Col>
        <Col xs={5} md={3}>
          <SelectCompany
            name="companyId"
            lessorId={form.lessorId}
            value={form.companyId}
            errors={errors}
            onChange={onCompanyChange}
          />
        </Col>
        <Col xs={6} md={3}>
          <SelectVendor
            name="vendorId"
            lessorId={form.lessorId}
            companyId={form.companyId}
            value={form.vendorId}
            errors={errors}
            onChange={onVendorChange}
          />
        </Col>
        <Col xs={6} md={6}>
          <SelectContract
            name="contractId"
            lessorId={form.lessorId}
            companyId={form.companyId}
            vendorId={form.vendorId}
            value={form.contractId}
            errors={errors}
            onChange={onContractChange}
          />
        </Col>

        <Col sm={12}>
          <Text bold color="#000000" paddingTop={30}>
            Share details of your message
          </Text>
        </Col>

        <Col md={6}>
          <Select
            label="Category"
            name="category"
            placeholder="Select category"
            value={form.category}
            options={categoryOptions}
            errors={errors}
            helpText="*Please select the closest possible category"
            onChange={onCategoryChange}
          />
        </Col>

        <Col md={6}>
          <Select
            label="Type"
            name="type"
            placeholder={typePlaceholder}
            value={
              typeOptions.length === 1 && !typeOptions[0].label
                ? null
                : form.type
            }
            options={typeOptions}
            disabled={!form.category || typeOptions.length === 1}
            errors={errors}
            helpText="*Please select the closest possible type"
            onChange={onChange}
          />
        </Col>

        <Col sm={12}>
          <Input
            name="concern"
            value={form.concern}
            label="Message"
            placeholder="Enter your message here"
            errors={errors}
            textarea={true}
            onChange={onChange}
            counter="characters"
            maxlength={1000}
            rows={6}
          />
        </Col>

        <Col sm={12}>
          <FileHelp
            title={
              <Text color="#000000">
                <span className="mr-2">Supporting documents (optional)</span>
                {toolTipContent && (
                  <Tooltip
                    title={
                      <>
                        <Text
                          color="#5A7188"
                          paddingTop={10}
                          paddingLeft={10}
                          paddingBottom={10}
                          paddingRight={10}
                        >
                          Sample supporting documents:
                        </Text>
                        <HTMLViewer
                          margin="0 0 0 10px"
                          data={toolTipContent}
                          color="#5A7188"
                          padding="0 0 10px 0"
                        />
                      </>
                    }
                    trigger="hover"
                  >
                    {''}
                    <IconInfomation />
                  </Tooltip>
                )}
              </Text>
            }
            items={[
              '• File must be less than 20MB',
              '• File types of .pdf, .jpg , xls or .xlsx',
            ]}
          ></FileHelp>
        </Col>

        <Col sm={12} md={9}>
          <FileDropZone
            accept={accept}
            multiple={multiple}
            onChange={onFileChange}
            style={{
              backgroundColor: '#FAFAFA',
              border: '0.5 dashed #707070',
            }}
            label="Select from your local folder"
          />

          {files.map((file, index) => (
            <FileItem
              key={index}
              name={file.name}
              size={file.size}
              error={file.error}
              status={file.status}
              percent={file.percent}
              onRemove={() => removeFile(file.id)}
            />
          ))}
        </Col>

        <Col sm={12}>
          <Space style={{ marginTop: isMobile ? '-10px' : '40px' }}>
            <Button disabled={uploading} width="200px" onClick={onSubmit}>
              Submit
            </Button>
          </Space>
        </Col>
      </Row>
    </Box>
  )
}

TicketForm.propTypes = {}

TicketForm.defaultProps = {}

export default TicketForm
